import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import CalendarPicker from '../util/CalendarPicker';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { transformToHierarchy } from '../util/tree';
import CategoryTaskPicker from '../timesheetsComp/CategoryTaskPicker';

interface Props {
  setShow: (v: boolean) => void;
  refreshIncome: () => void;
  record: any;          // строка income из таблицы (нужен record.id)
}

export default function EditIncome({ setShow, refreshIncome, record }: Props) {
  const [date, setDate] = useState<Date>(new Date());
  const [amount, setAmount] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [allCategory, setAllCategory] = useState<any[]>([]);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    (async () => {
      try {
        const { data: cats } = await axios.get('category');
        setAllCategory(cats || []);
        const { data: rec } = await axios.get(`finance-income/${record.id}`);
        const src = rec || record;
        setDate(src.date ? new Date(src.date) : new Date());
        setAmount(src.amount != null ? String(src.amount) : '');
        setName(src.name || '');
        setDescription(src.description || '');
        setCategoryId(src.categoryId ?? null);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [record]);

  const handleSave = async () => {
    if (!amount || !date) {
      enqueueSnackbar(t('FillRequired'), { variant: 'warning' });
      return;
    }
    try {
      await axios.put(`finance-income/${record.id}`, {
        amount: Number(amount),
        date,
        name: name || null,
        description: description || null,
        categoryId: categoryId || null,
      });
      enqueueSnackbar(t('SavedSuccessfully'), { variant: 'success' });
      refreshIncome();
      setShow(false);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || 'Error', { variant: 'error' });
    }
  };

  const catTree = transformToHierarchy(allCategory);

  return (
    <div className='fx-modal' role="presentation">
      {/* Header */}
      <div className='fx-head'>
        <div className='fx-head-text'>
          <div className='fx-head-title'>{t('AddIncome')}</div>
          <div className='fx-head-sub'>{t('AddIncomeSub')}</div>
        </div>
        <div className='fx-close' onClick={() => setShow(false)}><ExitToAppIcon fontSize="small" /></div>
      </div>

      {/* Body */}
      <div className='fx-body'>
        {/* Date */}
        <div className='fx-field'>
          <p className='fx-label'>{t('Date')} <span className='fx-req'>*</span></p>
          <CalendarPicker
            mode="single"
            variant="bare"
            className='fx-input'
            value={date}
            onChange={(d: Date) => setDate(d)}
            displayFormat="DD.MM.YYYY"
          />
        </div>

        {/* Category */}
        <div className='fx-field'>
          <p className='fx-label'>{t('Category')}</p>
          <CategoryTaskPicker
            nodes={catTree}
            selectedCategoryId={categoryId != null ? [categoryId] : []}
            categoryName={[]}
            onCategorySelect={(id) => setCategoryId(id === -1 ? null : id)}
            selectedTasks={[]}
            setSelectedTasks={() => {}}
            taskOrTasks={false}
            categoryOnly
            block
          />
        </div>

        {/* Description */}
        <div className='fx-field'>
          <p className='fx-label'>{t('Description')}</p>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className='fx-textarea'
          />
        </div>

        {/* Name */}
        <div className='fx-field'>
          <p className='fx-label'>{t('Name')}</p>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className='fx-input'
          />
        </div>

        {/* Amount */}
        <div className='fx-field'>
          <p className='fx-label'>{t('Amount')} <span className='fx-req'>*</span></p>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className='fx-input'
            placeholder="0.00"
          />
        </div>
      </div>

      {/* Footer */}
      <div className='fx-foot'>
        <button type="button" className='fx-save' onClick={handleSave}>
          {t('Save')}
        </button>
        <button type="button" className='fx-cancel' onClick={() => setShow(false)}>
          {t('Cancel')}
        </button>
      </div>
    </div>
  );
}
